import React, { useContext } from 'react'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import { makeStyles } from '@material-ui/core/styles'
import SearchOutlinedIcon from 'components/material-symbol/icons/Search'
import AddIcon from 'components/material-symbol/icons/Add'
import MailOutlineIcon from 'components/material-symbol/icons/MailOutline'
import VisibilityOffOutlinedIcon from 'components/material-symbol/icons/VisibilityOff'
import NewConversationDialog from './new-conversation-dialog'
import { ChannelListContext } from './channel-list-context'

const TAB_PRIMARY_BLUE = '#0053FF'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.common.white,
    overflow: 'visible',
  },
  tabBar: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(4.5),
    minHeight: 48,
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'visible',
  },
  tabButton: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: 48,
    margin: 0,
    padding: 0,
    border: 'none',
    borderRadius: 0,
    background: 'transparent',
    cursor: 'pointer',
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1.5,
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
    '&:active': {
      backgroundColor: 'rgba(0, 83, 255, 0.08)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 3,
      backgroundColor: 'transparent',
    },
  },
  tabButtonSelected: {
    color: TAB_PRIMARY_BLUE,
    '&:hover': {
      color: TAB_PRIMARY_BLUE,
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
    '&::after': {
      backgroundColor: TAB_PRIMARY_BLUE,
    },
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    maxWidth: '100%',
  },
  tabLabelText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.5,
  },
  tabCount: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
  tabIcon: {
    flexShrink: 0,
    fontSize: 20,
    width: 20,
    height: 20,
  },
  tabIconSelected: {
    color: TAB_PRIMARY_BLUE,
  },
  searchSection: {
    padding: theme.spacing(1.5, 2, 1.5),
  },
  searchField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 9999,
      backgroundColor: theme.palette.common.white,
      minHeight: 44,
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(1.5),
      overflow: 'hidden',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
      borderRadius: 9999,
    },
    '& fieldset': {
      borderRadius: 9999,
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(1.25, 0),
      fontSize: '0.875rem',
    },
    '& .MuiInputAdornment-root': {
      marginRight: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
  },
  newMessageSection: {
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: 0,
    padding: 0,
  },
  newMessageLink: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    border: 'none',
    background: 'none',
    color: '#0053FF',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: 'rgba(0, 83, 255, 0.04)',
    },
    '& .MuiSvgIcon-root': {
      color: '#0053FF',
    },
  },
}))

const TabLabel = ({ icon: Icon, label, count, selected }) => {
  const classes = useStyles()
  return (
    <span className={classes.tabLabel}>
      <Icon
        className={clsx(classes.tabIcon, {
          [classes.tabIconSelected]: selected,
        })}
        htmlColor={selected ? TAB_PRIMARY_BLUE : '#172024'}
      />
      <span className={classes.tabLabelText}>
        <span>{label}</span>
        <span className={classes.tabCount}>{count}</span>
      </span>
    </span>
  )
}

const ChannelListHeader = React.memo(() => {
  const classes = useStyles()
  const {
    searchQuery,
    setSearchQuery,
    listTab,
    setListTab,
    inboxCount,
    hiddenCount,
  } = useContext(ChannelListContext)

  return (
    <Box className={`${classes.root} str-chat__channel-list-header`}>
      <Box
        className={`${classes.tabBar} messaging-channel-list-tabs`}
        role="tablist"
        aria-label="message list tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={listTab === 'inbox'}
          className={clsx(classes.tabButton, {
            [classes.tabButtonSelected]: listTab === 'inbox',
          })}
          onClick={() => setListTab('inbox')}
        >
          <TabLabel
            icon={MailOutlineIcon}
            label="Inbox"
            count={inboxCount}
            selected={listTab === 'inbox'}
          />
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listTab === 'hidden'}
          className={clsx(classes.tabButton, {
            [classes.tabButtonSelected]: listTab === 'hidden',
          })}
          onClick={() => setListTab('hidden')}
        >
          <TabLabel
            icon={VisibilityOffOutlinedIcon}
            label="Hidden"
            count={hiddenCount}
            selected={listTab === 'hidden'}
          />
        </button>
      </Box>
      <Box className={classes.searchSection}>
        <TextField
          className={classes.searchField}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          color="primary"
          variant="outlined"
          fullWidth
          placeholder="Search messages..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {listTab !== 'hidden' && (
        <Box
          className={`${classes.newMessageSection} messaging-channel-list-new-message`}
        >
          <NewConversationDialog
            trigger={
              <button type="button" className={classes.newMessageLink}>
                <AddIcon fontSize="small" htmlColor="#0053FF" />
                <span>Start a New Message</span>
              </button>
            }
          />
        </Box>
      )}
    </Box>
  )
})

export default ChannelListHeader
